<script setup lang="ts">
import { AppText } from '@shared/ui/typography'
import { AppTable, AppTd, AppTh, AppTr } from '@shared/ui/tables'

import { useTableSubscriptions } from '../model/table-subscriptions.hook'
import SubscriptionPayments from './SubscriptionPayments.vue'
import SubscriptionRow from './SubscriptionRow.vue'

defineOptions({
  name: 'TableSubscriptions',
})

const props = withDefaults(defineProps<{
  /**
   * Off on the customer's own Subscriptions tab, where every row is that customer. That tab also keeps the
   * legacy layout: the actions menu first, fixed column widths and wrapping headers.
   */
  withCustomer?: boolean
}>(), {
  withCustomer: true,
})

const {
  subscriptions,
  columns,
  isLoading,
  hasSubscriptions,
  canManageSubscriptions,
  canManageOrders,
  meta,
  perPageOptions,
  isExpanded,
  setChangePage,
  setChangePerPage,
} = useTableSubscriptions({ withCustomer: props.withCustomer })
</script>

<template>
  <div :class="['table-subscriptions', { 'table-subscriptions--customer-tab': !withCustomer }]">
    <AppTable
      class="rounded-table"
      :meta="meta"
      :per-page-options="perPageOptions"
      :loading="isLoading"
      @change:page="setChangePage"
      @change:per-page="setChangePerPage"
    >
      <template #head>
        <AppTr>
          <AppTh
            v-for="column in columns"
            :key="column.key"
            :class="`table-subscriptions__cell--${column.key}`"
          >
            <span
              v-if="column.isLabelHidden"
              class="table-subscriptions__sr-only"
            >
              {{ column.label }}
            </span>
            <AppText
              v-else
              name="body1-medium"
              color="slate-900"
            >
              {{ column.label }}
            </AppText>
          </AppTh>
        </AppTr>
      </template>

      <template
        v-if="hasSubscriptions"
        #body
      >
        <template
          v-for="subscription in subscriptions"
          :key="subscription.id"
        >
          <SubscriptionRow
            :subscription="subscription"
            :can-manage="canManageSubscriptions"
            :with-customer="withCustomer"
          />
          <AppTr v-if="isExpanded(subscription)">
            <AppTd
              class="table-subscriptions__payments"
              :colspan="columns.length"
            >
              <SubscriptionPayments
                :subscription="subscription"
                :can-manage-orders="canManageOrders"
              />
            </AppTd>
          </AppTr>
        </template>
      </template>
    </AppTable>
  </div>
</template>

<style scoped lang="scss" src="./TableSubscriptions.scss" />
